import { Add, Close, Delete, Info, Visibility, VisibilityOff } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { patch } from "../../../../../api/api";
import { fetchCustomers } from "../../../../../api/customers";
import { bulkLinkCustomers, fetchProfile } from "../../../../../api/profile";
import { useCurrentProfile } from "../../../../../ContextWrapper";
import {
    getAllowedAccountRoles,
    getExistingRoleHolder,
    getExternalUserTypesDisplay,
    getInternalUserTypesDisplay,
    getUserCategoryForBackendType,
    INCOMPLETE_ROLE_LABEL,
    isInternalUserTypeDisplay,
    isRoleAllowedForUserType,
    mapRoleLabelToBackend,
    mapUserTypeBackendToDisplay,
    mapUserTypeDisplayToBackend,
    normalizeAccountRoleLabel
} from "../../../../../utils/userRoleUtils";

export default function UserEditDialog({ data, onClose, onSubmit, onShowSuccess }) {
  const navigate = useNavigate();
  const { profile } = useCurrentProfile();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    userCategory: "",
    userType: "",
    linkedAccounts: []
  });

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [newAccount, setNewAccount] = useState({
    accountName: "",
    userType: "",
    customerId: null,
    bookkeeper: null,
    manager: null,
  });
  const [passwordReset, setPasswordReset] = useState(false);
  const [manualPasswordChange, setManualPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [linkedAccountsOpen, setLinkedAccountsOpen] = useState(false);
  
  const isAdmin = profile?.type === "admin";
  
  // Password validation requirements
  const getPasswordRequirements = (password, confirmPassword) => {
    const hasMinLength = password.length >= 8;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasCapital = /[A-Z]/.test(password);
    const passwordsMatch = password === confirmPassword && password.length > 0;

    const isValid =
      hasMinLength &&
      hasSpecialChar &&
      hasNumber &&
      hasCapital &&
      passwordsMatch;

    return {
      hasMinLength,
      hasSpecialChar,
      hasNumber,
      hasCapital,
      passwordsMatch,
      isValid,
    };
  };
  
  const [originalData, setOriginalData] = useState({});
  const [originalLinkedAccounts, setOriginalLinkedAccounts] = useState([]);

  const userCategoryOptions = ["Internal", "External"];

  const internalUserTypes = useMemo(() => getInternalUserTypesDisplay(), []);
  const externalUserTypes = useMemo(() => getExternalUserTypesDisplay(), []);

  const getUserTypeOptions = useCallback((category) => {
    if (category === "Internal") {
      return internalUserTypes.map((type) => ({ value: type, label: type }));
    } else if (category === "External") {
      return externalUserTypes.map((type) => ({ value: type, label: type }));
    }
    return [];
  }, [internalUserTypes, externalUserTypes]);

  const userTypeOptions = useMemo(
    () => getUserTypeOptions(formData.userCategory),
    [formData.userCategory, getUserTypeOptions]
  );

  const allowedAccountRoles = useMemo(() => (
    formData.userType ? getAllowedAccountRoles(formData.userType) : []
  ), [formData.userType]);

  const alignRoleToUserType = useCallback((role, userType) => {
    if (!role || role === INCOMPLETE_ROLE_LABEL) {
      return INCOMPLETE_ROLE_LABEL;
    }

    const normalized = normalizeAccountRoleLabel(role);
    
    if (!normalized) {
      return INCOMPLETE_ROLE_LABEL;
    }

    if (!userType) {
      return normalized;
    }

    const isAllowed = isRoleAllowedForUserType(normalized, userType);
    
    return isAllowed ? normalized : INCOMPLETE_ROLE_LABEL;
  }, []);

  const handleLinkedAccountChange = useCallback((index, updates) => {
    setFormData((prev) => {
      const updatedLinkedAccounts = prev.linkedAccounts.map((account, accountIndex) => {
        if (accountIndex !== index) {
          return account;
        }

        const nextAccount = { ...account, ...updates };

        if (updates.userType !== undefined || updates.displayRole !== undefined) {
          const desiredRole = updates.userType ?? updates.displayRole ?? nextAccount.userType;
          const alignedRole = alignRoleToUserType(desiredRole, prev.userType);
          nextAccount.userType = alignedRole;
          nextAccount.displayRole = alignedRole;
        }

        return nextAccount;
      });

      return {
        ...prev,
        linkedAccounts: updatedLinkedAccounts,
      };
    });
  }, [alignRoleToUserType]);

  useEffect(() => {
    // Fetch full profile data with customers when dialog opens
    const fetchFullProfileData = async () => {
      if (data && data.id) {
        try {
          const fullProfile = await fetchProfile(data.id, { include_linked_accounts: true });
          initializeDialogData(fullProfile);
        } catch (error) {
          console.error("Error fetching full profile data:", error);
          // Fall back to using existing data if fetch fails
          initializeDialogData(data.originalProfile);
        }
      }
    };

    const initializeDialogData = (profileData) => {
      if (!profileData) return;

      // Extract name from the data
      const name = data.name || "";
      const email = data.email || "";
      const backendUserType = profileData?.type || null;
      const displayUserType =
        data.userType || mapUserTypeBackendToDisplay(backendUserType) || "";

      const derivedCategory =
        getUserCategoryForBackendType(backendUserType) ||
        (displayUserType
          ? isInternalUserTypeDisplay(displayUserType)
            ? "Internal"
            : "External"
          : null);
      const userCategory = derivedCategory || "External";
      
      // Use customers_with_roles from ProfileCustomers
      const rawLinkedAccounts = profileData?.customers_with_roles
        ?.filter(customer => customer.is_active === true)
        ?.map(customer => {
          const role = customer.role || null;
          const normalizedRole = normalizeAccountRoleLabel(role) || INCOMPLETE_ROLE_LABEL;
          
          return {
            id: customer.id,
            accountName: customer.name || "Incomplete Account",
            userType: normalizedRole,
            displayRole: normalizedRole,
            customerId: customer.id,
            bookkeeper: customer.bookkeeper || null,
            manager: customer.manager || null,
          };
        }) || [];

      const linkedAccounts = rawLinkedAccounts.map((account) => {
        const alignedRole = alignRoleToUserType(account.userType, displayUserType);
        
        return {
          ...account,
          userType: alignedRole,
          displayRole: alignedRole,
        };
      });
      
      const initialFormData = {
        name: name,
        email: email,
        userCategory: userCategory,
        userType: displayUserType,
        linkedAccounts: linkedAccounts
      };

      setFormData(initialFormData);
      setOriginalData(initialFormData);
      setOriginalLinkedAccounts(rawLinkedAccounts);
    };

    if (data) {
      fetchFullProfileData();
    }
  }, [data, alignRoleToUserType]);

  useEffect(() => {
    setNewAccount((prev) => {
      const nextRole =
        prev.userType && allowedAccountRoles.includes(prev.userType)
          ? prev.userType
          : "";

      if (prev.userType === nextRole) {
        return prev;
      }

      return {
        ...prev,
        userType: nextRole,
      };
    });
  }, [allowedAccountRoles]);

  const fetchCustomersList = async (searchTerm = "") => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomers([]);
      setLoadingCustomers(false);
      return;
    }
    
    setLoadingCustomers(true);
    try {
      // Server-side search with limited results for better performance
      const params = {
        search: searchTerm.trim(),
        page_size: 10  // Limit to top 10 results
      };
      
      const response = await fetchCustomers(profile, params);
      // Only show active customers in the dropdown
      const activeCustomers = (response.results || []).filter(customer => customer.is_active === true)
        .map(customer => ({
          ...customer,
          optionType: "account"
        }));

      if (activeCustomers.length === 0) {
        setCustomers([
          {
            id: "__no_account_results__",
            optionType: "noResults",
            searchTerm
          }
        ]);
      } else {
        setCustomers(activeCustomers);
      }
    } catch (error) {
      setCustomers([
        {
          id: "__no_account_results__",
          optionType: "noResults",
          searchTerm
        }
      ]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    if (field === "userCategory") {
      setFormData((prev) => ({
      ...prev,
        userCategory: value,
        userType: "",
        linkedAccounts: prev.linkedAccounts.map((account) => ({
          ...account,
          userType: INCOMPLETE_ROLE_LABEL,
        })),
      }));

      setNewAccount({
        accountName: "",
        userType: "",
        customerId: null,
        bookkeeper: null,
        manager: null,
      });

      return;
    }

    if (field === "userType") {
      const allowedForValue = getAllowedAccountRoles(value);

      setFormData((prev) => {
        const updatedLinkedAccounts = prev.linkedAccounts.map((account) => {
          const alignedRole = alignRoleToUserType(account.userType, value);
          return {
            ...account,
            userType: alignedRole,
            displayRole: alignedRole,
          };
        });

        return {
          ...prev,
          userType: value,
          linkedAccounts: updatedLinkedAccounts,
        };
      });

      setNewAccount((prev) => ({
        ...prev,
        userType: prev.userType && allowedForValue.includes(prev.userType) ? prev.userType : "",
      }));

      return;
    }

        setFormData(prev => ({
          ...prev,
      [field]: value
    }));
  };

  const handleAddAccount = () => {
    if (!newAccount.accountName || !newAccount.userType) {
      return;
    }

    if (!allowedAccountRoles.includes(newAccount.userType)) {
      return;
    }

    const trimmedAccountName = newAccount.accountName.trim();
    if (!trimmedAccountName) {
      return;
    }

    const isAlreadyLinked = formData.linkedAccounts.some((account) => {
      const existingId = account.customerId || account.id;
      if (existingId && newAccount.customerId && existingId === newAccount.customerId) {
        return true;
      }
      return account.accountName === trimmedAccountName;
    });
      
    if (isAlreadyLinked) {
      return;
    }

    setFormData((prev) => ({
          ...prev,
      linkedAccounts: [
        ...prev.linkedAccounts,
        {
          id: newAccount.customerId || Date.now(),
          customerId: newAccount.customerId,
          accountName: trimmedAccountName,
          userType: newAccount.userType,
          displayRole: newAccount.userType,
          bookkeeper: newAccount.bookkeeper,
          manager: newAccount.manager,
        },
      ],
    }));

    setNewAccount({
      accountName: "",
      userType: "",
      customerId: null,
      bookkeeper: null,
      manager: null,
    });
  };

  const handleRemoveAccount = (accountId) => {
    setFormData(prev => ({
      ...prev,
      linkedAccounts: prev.linkedAccounts.filter(account => account.id !== accountId)
    }));
  };

  const handleCreateNewAccount = () => {
    onClose();
    navigate('/management/accounts');
    setTimeout(() => {
      const event = new CustomEvent('openAccountCreateDialog');
      window.dispatchEvent(event);
    }, 100);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.userType) {
      return;
    }

    try {
      const originalCustomerIds = originalLinkedAccounts.map(account => account.customerId || account.id);
      const currentCustomerIds = formData.linkedAccounts.map(account => account.customerId || account.id);

      // Find customers to unlink (in original but not in current)
      const customersToUnlink = originalCustomerIds.filter(id => !currentCustomerIds.includes(id));
      
      // Find customers to link (in current but not in original)
      const customersToLink = currentCustomerIds.filter(id => !originalCustomerIds.includes(id));

      const originalRoleLookup = originalLinkedAccounts.reduce((acc, account) => {
        const key = account.customerId || account.id;
        if (!key) {
          return acc;
        }

        const normalizedRole = normalizeAccountRoleLabel(account.userType) || INCOMPLETE_ROLE_LABEL;
        acc[key] = normalizedRole;
        return acc;
      }, {});

      const roleChangeCustomers = formData.linkedAccounts
        .filter((account) => {
          const key = account.customerId || account.id;
          if (!key) {
            return false;
          }

          const previousRole = originalRoleLookup[key];
          const currentRole = alignRoleToUserType(account.userType, formData.userType);

          if (currentRole === INCOMPLETE_ROLE_LABEL) {
            return false;
          }

          return previousRole && previousRole !== currentRole;
        })
        .map((account) => account.customerId || account.id);

      // Build account roles map for newly linked accounts
      const validRolesByCustomer = {};
      formData.linkedAccounts.forEach(account => {
        const customerId = account.customerId || account.id;
        if (!customerId) {
          return;
        }

        const alignedRole = alignRoleToUserType(account.userType, formData.userType);
        if (
          alignedRole === INCOMPLETE_ROLE_LABEL ||
          !allowedAccountRoles.includes(alignedRole)
        ) {
          return;
        }

        validRolesByCustomer[customerId] = alignedRole;
      });

      const accountRoles = {};
      Object.entries(validRolesByCustomer).forEach(([customerId, normalizedRole]) => {
        accountRoles[customerId] = mapRoleLabelToBackend(normalizedRole);
      });

      // Use bulk endpoint for linking/unlinking customers
      const bulkCustomersToLink = Array.from(
        new Set([...customersToLink, ...roleChangeCustomers])
      ).filter((customerId) => !!validRolesByCustomer[customerId]);

      if (customersToUnlink.length > 0 || bulkCustomersToLink.length > 0) {
        try {
          await bulkLinkCustomers(data.originalProfile.id, bulkCustomersToLink, customersToUnlink, accountRoles);
        } catch (error) {
          // Continue with profile update even if linking fails
        }
      }

      // Then update other profile data
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const resolvedBackendType =
        mapUserTypeDisplayToBackend(formData.userType) ||
        data.originalProfile?.type ||
        null;

      if (!resolvedBackendType) {
        return;
      }

      const profileData = {
        email: formData.email,
        type: resolvedBackendType,
        first_name: firstName,
        last_name: lastName,
        ...(passwordReset && { request_password_reset: true }),
        ...(manualPasswordChange && newPassword && { 
          password: newPassword,
          manual_password_change: true 
        }),
        ...(formData.linkedAccounts.length > 0 && {
          customers: formData.linkedAccounts.map(account => ({ value: account.customerId || account.id }))
        })
      };

      const response = await patch(`/api/v2/profiles/${data.originalProfile.id}/`, profileData);
      const updatedProfile = response.data;
      onSubmit?.(updatedProfile);
      
      // Show success message before closing dialog
      if (onShowSuccess) {
        onShowSuccess("User updated successfully!");
      }
      
      onClose();
    } catch (error) {
      // Error handled silently
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const hasChanges = useCallback(() => {
    const formChanged = Object.keys(originalData).some(
      key => originalData[key] !== formData[key]
    );

    const linkedAccountsChanged = 
      originalLinkedAccounts.length !== formData.linkedAccounts.length ||
      originalLinkedAccounts.some(originalAccount => 
        !formData.linkedAccounts.some(currentAccount => 
          originalAccount.accountName === currentAccount.accountName && 
          originalAccount.userType === currentAccount.userType &&
          originalAccount.customerId === currentAccount.customerId
        )
      ) ||
      formData.linkedAccounts.some(currentAccount => 
        !originalLinkedAccounts.some(originalAccount => 
          originalAccount.accountName === currentAccount.accountName && 
          originalAccount.userType === currentAccount.userType &&
          originalAccount.customerId === currentAccount.customerId
        )
      );

    return formChanged || linkedAccountsChanged;
  }, [formData, originalData, originalLinkedAccounts]);

  const passwordRequirements = getPasswordRequirements(newPassword, confirmPassword);
  const passwordsValid = !manualPasswordChange || passwordRequirements.isValid;
  const isFormValid = formData.name && formData.email && formData.userType && passwordsValid;
  const isFormValidAndChanged = useMemo(() => {
    return isFormValid && (hasChanges() || passwordReset || (manualPasswordChange && passwordRequirements.isValid));
  }, [isFormValid, hasChanges, passwordReset, manualPasswordChange, passwordRequirements.isValid]);

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="md" 
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{
        sx: { borderRadius: 2, minHeight: "600px" }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Edit User
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Here you can view account details, track connections, and edit essentials.
        </Typography>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Create Date and Latest Login */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              Create Date: {formatDateOnly(data.originalProfile?.created_on)}
            </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: data.latestLoginColor || "#6b7280",
                  fontWeight: 500,
                  fontSize: "0.875rem"
                }}
              >
                Latest Login: {data.latestLogin || "-"}
              </Typography>
          </Box>

          {/* Primary Fields */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Primary Fields
            </Typography>
            <Grid container rowSpacing={3} columnSpacing={2}>
              <Grid size={6}>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={handleChange("name")}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter full name"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter email address"
                />
              </Grid>
              
              <Grid size={6}>
                <FormControl required fullWidth size="small">
                  <InputLabel>User Category</InputLabel>
                  <Select
                    value={formData.userCategory}
                    onChange={handleChange("userCategory")}
                    label="User Category"
                    MenuProps={{ sx: { zIndex: 99999 } }}
                    renderValue={(selected) => {
                      return <span style={{ fontSize: '0.875rem' }}>{selected}</span>;
                    }}
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    {userCategoryOptions.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={6}>
                <FormControl required fullWidth size="small">
                  <InputLabel>Primary User Type</InputLabel>
                  <Select
                    value={formData.userType}
                    onChange={handleChange("userType")}
                    label="Primary User Type"
                    disabled={!formData.userCategory}
                    MenuProps={{ sx: { zIndex: 99999 } }}
                    renderValue={(selected) => {
                      return <span style={{ fontSize: '0.875rem' }}>{selected}</span>;
                    }}
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    {userTypeOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                            {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Linked Accounts */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
              Linked Accounts
            </Typography>
            
            {/* Existing Linked Accounts */}
            {formData.linkedAccounts.map((account, index) => {
              const roleValue = account.userType || account.displayRole || "";
              const isIncompleteRecord = roleValue === INCOMPLETE_ROLE_LABEL;
              const existingRoleHolder = getExistingRoleHolder(account, roleValue);

              return (
                <Grid container columnSpacing={2} alignItems="flex-start" key={account.id} sx={{ mb: 2 }}>
                  <Grid size={6}>
                <TextField
                  label="Account"
                  value={account.accountName}
                  size="small"
                      fullWidth
                      onChange={(event) =>
                        handleLinkedAccountChange(index, {
                          accountName: event.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid size={6}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel shrink={!isIncompleteRecord || roleValue !== ""}>
                            Account Role
                          </InputLabel>
                          <Select
                            value={isIncompleteRecord ? "" : roleValue}
                  label="Account Role"
                            onChange={(event) => {
                              const selectedRole = event.target.value;
                              handleLinkedAccountChange(index, {
                                userType: selectedRole,
                                displayRole: selectedRole,
                              });
                            }}
                            disabled={allowedAccountRoles.length === 0}
                            MenuProps={{ sx: { zIndex: 99999 } }}
                            displayEmpty
                            notched={!isIncompleteRecord || roleValue !== ""}
                            renderValue={(selected) => {
                              if (!selected || selected === "") {
                                return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{INCOMPLETE_ROLE_LABEL}</span>;
                              }
                              return selected;
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select a role</em>
                            </MenuItem>
                            {allowedAccountRoles.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {existingRoleHolder && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#ef4444', 
                              fontStyle: 'italic',
                              display: 'block',
                              mt: 0.5,
                              fontSize: '0.75rem'
                            }}
                          >
                            Overriding existing {roleValue}: {existingRoleHolder}
                          </Typography>
                        )}
                      </Box>
                <IconButton 
                  onClick={() => handleRemoveAccount(account.id)}
                  size="small"
                        sx={{ color: "#ef4444", mt: 0.5 }}
                >
                  <Delete />
                </IconButton>
              </Box>
                  </Grid>
                </Grid>
              );
            })}
            
            {/* Add New Account */}
            <Grid container columnSpacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
              <Grid size={6}>
              <Autocomplete
                freeSolo
                options={customers.filter((customer) => {
                  if (customer.optionType === "noResults") return true;
                  return !formData.linkedAccounts.some(
                    (linkedAccount) => (linkedAccount.customerId || linkedAccount.id) === customer.id
                  );
                })}
                value={null}
                inputValue={newAccount.accountName}
                loading={loadingCustomers}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.optionType === 'noResults') return option.searchTerm || '';
                  return option.name || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option) return false;
                  if (typeof value === "string") {
                    return option.name === value;
                  }
                  if (value && value.id) {
                  return option.id === value.id;
                  }
                  return false;
                }}
                onInputChange={(event, newValue, reason) => {
                  if (reason === 'input') {
                    setNewAccount((prev) => ({ 
                      ...prev, 
                      accountName: newValue || '', 
                      customerId: null, 
                      bookkeeper: null, 
                      manager: null 
                    }));
                    if (newValue && newValue.length >= 2) {
                      setLinkedAccountsOpen(true);
                      fetchCustomersList(newValue);
                    } else {
                      setCustomers([]);
                      setLinkedAccountsOpen(false);
                    }
                  } else if (reason === 'clear') {
                    setNewAccount((prev) => ({ 
                      ...prev, 
                      accountName: '', 
                      customerId: null, 
                      bookkeeper: null, 
                      manager: null 
                    }));
                    setCustomers([]);
                    setLinkedAccountsOpen(false);
                  }
                }}
                onChange={(event, newValue, reason) => {
                  if (newValue && typeof newValue === 'object') {
                    if (newValue.optionType === 'noResults') {
                      handleCreateNewAccount();
                      setCustomers([]);
                      setNewAccount((prev) => ({ 
                        ...prev, 
                        accountName: '', 
                        customerId: null, 
                        bookkeeper: null, 
                        manager: null 
                      }));
                      setLinkedAccountsOpen(false);
                      return;
                    }
                    setNewAccount((prev) => ({
                      ...prev,
                      accountName: newValue.name,
                      customerId: newValue.id,
                      bookkeeper: newValue.bookkeeper || null,
                      manager: newValue.manager || null,
                    }));
                    setCustomers([]);
                    setLinkedAccountsOpen(false);
                  } else if (newValue === null || newValue === '') {
                    setNewAccount((prev) => ({ 
                      ...prev, 
                      accountName: '', 
                      customerId: null, 
                      bookkeeper: null, 
                      manager: null 
                    }));
                    setCustomers([]);
                    setLinkedAccountsOpen(false);
                  } else if (typeof newValue === 'string') {
                    setNewAccount((prev) => ({ 
                      ...prev, 
                      accountName: newValue, 
                      customerId: null, 
                      bookkeeper: null, 
                      manager: null 
                    }));
                  }
                }}
                filterOptions={(options) => options}
                open={linkedAccountsOpen}
                onOpen={() => {
                  if (newAccount.accountName && newAccount.accountName.length >= 2) {
                    setLinkedAccountsOpen(true);
                  }
                }}
                onClose={() => {
                  // prevent MUI from closing automatically; state drives visibility
                }}
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                onBlur={() => {
                  setCustomers([]);
                  setLinkedAccountsOpen(false);
                }}
                loadingText="Searching accounts..."
                noOptionsText="Type at least 2 characters to search accounts..."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account"
                    size="small"
                  />
                )}
                renderOption={(props, option) => {
                  if (option.optionType === 'noResults') {
                    return (
                      <li {...props}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                            No existing account matches "<em>{option.searchTerm}</em>"
                          </Typography>
                          <Divider />
                          <Box
                            sx={{ px: 2, py: 1, cursor: 'pointer' }}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              handleCreateNewAccount();
                              setCustomers([]);
                            }}
                          >
                            <Typography variant="body2" sx={{ color: '#627CFB', fontWeight: 500 }}>
                              + Create New Account
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    );
                  }

                  return (
                    <li {...props}>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          {option.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {option.primary_contact && `Contact: ${option.primary_contact}`}
                          {option.email && ` • ${option.email}`}
                        </div>
                      </div>
                    </li>
                  );
                }}
                slotProps={{
                  popper: {
                  placement: 'bottom-start',
                  style: { 
                    zIndex: 999999,
                    position: 'fixed'
                  },
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                    {
                      name: 'preventOverflow',
                      enabled: false,
                    },
                  ],
                  },
                }}
              />
              </Grid>
              <Grid size={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FormControl size="small" sx={{ flex: 1, zIndex: 99999 }}>
                <InputLabel>Account Role</InputLabel>
                <Select
                  value={newAccount.userType}
                  onChange={(event) => {
                    setNewAccount(prev => ({ ...prev, userType: event.target.value }));
                  }}
                  label="Account Role"
                      disabled={allowedAccountRoles.length === 0}
                  MenuProps={{ sx: { zIndex: 99999 } }}
                >
                      <MenuItem value="">
                        <em>Select a role</em>
                      </MenuItem>
                      {allowedAccountRoles.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton 
                onClick={handleAddAccount}
                    disabled={
                      !newAccount.userType ||
                      !allowedAccountRoles.includes(newAccount.userType) ||
                      !newAccount.accountName.trim()
                    }
                size="small"
                    sx={{ color: "#627CFB !important" }}
              >
                <Add />
              </IconButton>
            </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Password Reset */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
              Password Reset
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* Request Password Change Checkbox */}
            <FormControlLabel
              control={
                  <Checkbox
                  checked={passwordReset}
                    onChange={(e) => {
                      setPasswordReset(e.target.checked);
                      if (e.target.checked) {
                        setManualPasswordChange(false);
                        setNewPassword("");
                        setConfirmPassword("");
                        setShowNewPassword(false);
                        setShowConfirmPassword(false);
                      }
                    }}
                />
              }
              label="Request Password Change"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                    color: '#111827'
                }
              }}
            />
              
              {/* Manually Change Password Checkbox - Admin Only */}
              {isAdmin && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={manualPasswordChange}
                      onChange={(e) => {
                        setManualPasswordChange(e.target.checked);
                        if (e.target.checked) {
                          setPasswordReset(false);
                        } else {
                          setNewPassword("");
                          setConfirmPassword("");
                          setShowNewPassword(false);
                          setShowConfirmPassword(false);
                        }
                      }}
                    />
                  }
                  label="Manually Change Password"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: '#111827'
                    }
                  }}
                />
              )}
            </Box>
            
            {/* Password Input Fields */}
            {isAdmin && manualPasswordChange && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2, width: 'calc(50% - 4px)' }}>
                <TextField
                  label="New Password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={newPassword.length > 0 && !passwordRequirements.isValid}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size="small"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={confirmPassword.length > 0 && !passwordRequirements.passwordsMatch}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#171923' }}>
                                Password Requirements:
              </Typography>
                              <Stack spacing={0.5}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: passwordRequirements.hasMinLength ? '#38A169' : '#E53E3E',
                                    fontWeight: 500
                                  }}
                                >
                                  ✓ At least 8 characters
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: passwordRequirements.hasSpecialChar ? '#38A169' : '#E53E3E',
                                    fontWeight: 500
                                  }}
                                >
                                  ✓ Special character (!@#$%^&*...)
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: passwordRequirements.hasNumber ? '#38A169' : '#E53E3E',
                                    fontWeight: 500
                                  }}
                                >
                                  ✓ Number (0-9)
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: passwordRequirements.hasCapital ? '#38A169' : '#E53E3E',
                                    fontWeight: 500
                                  }}
                                >
                                  ✓ Capital letter (A-Z)
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: passwordRequirements.passwordsMatch ? '#38A169' : '#E53E3E',
                                    fontWeight: 500
                                  }}
                                >
                                  ✓ Passwords match
                                </Typography>
                              </Stack>
                            </Box>
                          }
                          placement="right-start"
                          arrow
                          PopperProps={{
                            sx: {
                              zIndex: 100000,
                              '& .MuiTooltip-tooltip': {
                                bgcolor: 'white',
                                color: '#171923',
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                border: '1px solid #E2E8F0',
                                borderRadius: '8px',
                                maxWidth: 'none'
                              },
                              '& .MuiTooltip-arrow': {
                                color: 'white',
                                '&::before': {
                                  border: '1px solid #E2E8F0'
                                }
                              }
                            }
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{ 
                              mr: 0.5,
                              color: newPassword.length > 0 && !passwordRequirements.isValid ? '#E53E3E' : '#666666'
                            }}
                          >
                            <Info sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            color: "#627CFB",
            borderColor: "#627CFB",
            "&:hover": {
              borderColor: "#4f46e5",
              backgroundColor: "rgba(98, 124, 251, 0.04)"
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isFormValidAndChanged}
          sx={{ 
            backgroundColor: "#627CFB",
            color: "white",
            "&:hover": {
              backgroundColor: "#4f46e5"
            }
          }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
